// ============================================================================
// NOTIFICATION SYSTEM TYPE DEFINITIONS
// ============================================================================

export type NotificationPriority = 'min' | 'low' | 'default' | 'high' | 'urgent';
export type NotificationType = 'system' | 'app' | 'user' | 'team';
export type NotificationStatus = 'unread' | 'read' | 'archived';
export type ChannelImportance = 'min' | 'low' | 'default' | 'high' | 'urgent';

// ============================================================================
// NOTIFICATION INTERFACES
// ============================================================================

export interface Notification {
  id: string;
  user_id: string;
  organization_id?: string;
  title: string;
  body?: string;
  category?: string;
  link_url?: string;
  
  // Read/Archive state
  is_read: boolean;
  read_at?: string;
  is_archived: boolean;
  
  // Metadata
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  
  // App identification
  app_id?: string;
  app_name?: string;
  app_icon?: string;
  
  // Classification
  notification_type?: NotificationType;
  priority?: NotificationPriority;
  status?: NotificationStatus;
  
  // Actions and media
  action_buttons?: NotificationAction[];
  avatar_url?: string;
  
  // Enhanced features (from new schema)
  scheduled_for?: string;
  delivered_at?: string;
  thread_id?: string;
  group_key?: string;
  channel_id?: string;
  sound?: string;
  requires_interaction?: boolean;
  auto_dismiss_after?: number;
  actions?: NotificationAction[];
  media?: NotificationMedia;
  delivery_stats?: NotificationDeliveryStats;
  snoozed_until?: string;
  expires_at?: string;
}

export interface NotificationAction {
  id: string;
  label: string;
  type?: 'button' | 'input' | 'link';
  variant?: 'default' | 'outline' | 'destructive' | 'ghost';
  icon?: string;
  handler?: string; // Function name or URL
  data?: Record<string, any>;
  placeholder?: string; // For input type
  url?: string; // For link type
}

export interface NotificationMedia {
  type: 'avatar' | 'image' | 'progress' | 'custom';
  url?: string;
  value?: number; // For progress bars
  max?: number; // For progress bars
  data?: Record<string, any>; // For custom media
}

export interface NotificationDeliveryStats {
  delivered_at?: string;
  opened_at?: string;
  dismissed_at?: string;
  actions_taken?: {
    action_id: string;
    taken_at: string;
  }[];
  focus_mode_blocked?: boolean;
  focus_mode_id?: string;
}

// ============================================================================
// NOTIFICATION CHANNEL INTERFACES
// ============================================================================

export interface NotificationChannel {
  id: string;
  organization_id?: string;
  app_id?: string;
  name: string;
  description?: string;
  icon_name: string;
  sound: string;
  importance: ChannelImportance;
  do_not_disturb_override: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChannelSettings {
  channel_id: string;
  user_id: string;
  enabled: boolean;
  sound?: string;
  banner_style?: 'banner' | 'list' | 'silent';
  volume?: number;
}

// ============================================================================
// FOCUS MODE INTERFACES
// ============================================================================

export interface FocusMode {
  id: string;
  user_id: string;
  organization_id?: string;
  name: string;
  icon_name: string;
  color: string;
  is_active: boolean;
  is_system: boolean; // System-defined modes cannot be deleted
  active_times: FocusModeSchedule[];
  allowed_channels: string[]; // Channel IDs
  blocked_channels: string[]; // Channel IDs
  do_not_disturb: boolean;
  auto_reply_message?: string;
  activated_at?: string;
  deactivated_at?: string;
  created_at: string;
  updated_at: string;
}

export interface FocusModeSchedule {
  start: string; // HH:MM format
  end: string; // HH:MM format
  days: number[]; // 0-6 (Sunday-Saturday)
  timezone?: string;
}

export interface FocusModeTemplate {
  name: string;
  icon_name: string;
  color: string;
  description: string;
  do_not_disturb: boolean;
  allowed_channel_types?: string[]; // Channel names like 'Alarms', 'Calls'
  blocked_channel_types?: string[];
}

// ============================================================================
// NOTIFICATION HISTORY INTERFACES
// ============================================================================

export interface NotificationHistoryEntry {
  id: string;
  notification_id: string;
  user_id: string;
  organization_id?: string;
  action: 'created' | 'delivered' | 'read' | 'dismissed' | 'snoozed' | 'action_taken';
  action_data?: Record<string, any>;
  focus_mode_active: boolean;
  focus_mode_id?: string;
  created_at: string;
}

// ============================================================================
// BUILDER PAYLOAD INTERFACES
// ============================================================================

export interface CreateNotificationPayload {
  // Required
  title: string;
  
  // User/Org
  userId: string;
  organizationId?: string;
  
  // Content
  body?: string;
  category?: string;
  linkUrl?: string;
  
  // Classification
  channel?: string; // Channel ID or name
  channelId?: string;
  priority?: NotificationPriority;
  notificationType?: NotificationType;
  
  // App identification
  appId?: string;
  appName?: string;
  appIcon?: string;
  
  // Grouping
  threadId?: string;
  groupKey?: string;
  
  // Behavior
  sound?: string;
  requiresInteraction?: boolean;
  autoDismissAfter?: number; // seconds
  
  // Actions and media
  actions?: NotificationAction[];
  media?: NotificationMedia;
  avatarUrl?: string;
  
  // Scheduling
  scheduledFor?: Date | string;
  expiresAt?: Date | string;
  
  // Legacy support
  actionButtons?: NotificationAction[];
  metadata?: Record<string, any>;
}

export interface ScheduleNotificationPayload extends CreateNotificationPayload {
  scheduledFor: Date | string;
  recurring?: {
    type: 'daily' | 'weekly' | 'monthly' | 'custom';
    interval?: number;
    endDate?: Date | string;
    cron?: string; // For custom schedules
  };
}

// ============================================================================
// NOTIFICATION MANAGER STATE INTERFACES
// ============================================================================

export interface NotificationManagerState {
  // Notifications
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error?: string;
  
  // Focus mode
  focusModes: FocusMode[];
  activeFocusMode?: FocusMode;
  
  // Channels
  channels: NotificationChannel[];
  channelSettings: Map<string, ChannelSettings>;
  
  // Grouping
  groupedNotifications: Map<string, Notification[]>; // By group_key
  threadedNotifications: Map<string, Notification[]>; // By thread_id
  
  // Scheduled
  scheduledNotifications: Notification[];
  
  // Real-time
  isSubscribed: boolean;
}

export interface NotificationManagerMethods {
  // Core operations
  createNotification: (payload: CreateNotificationPayload) => Promise<Notification>;
  scheduleNotification: (payload: ScheduleNotificationPayload) => Promise<Notification>;
  cancelScheduledNotification: (id: string) => Promise<void>;
  
  // State management
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismissNotification: (id: string) => Promise<void>;
  snoozeNotification: (id: string, durationMinutes: number) => Promise<void>;
  
  // Grouping
  getNotificationThread: (threadId: string) => Notification[];
  getNotificationGroup: (groupKey: string) => Notification[];
  
  // Actions
  executeNotificationAction: (notificationId: string, actionId: string) => Promise<void>;
  
  // Data refresh
  refetch: () => Promise<void>;
}

export interface NotificationManagerContext extends NotificationManagerState, NotificationManagerMethods {
  // Combined interface for the context
}

// ============================================================================
// FOCUS MODE MANAGER INTERFACES
// ============================================================================

export interface FocusModeManagerMethods {
  // Activation
  activateFocusMode: (modeId: string, durationMinutes?: number) => Promise<void>;
  deactivateFocusMode: () => Promise<void>;
  
  // CRUD operations
  createFocusMode: (config: Partial<FocusMode>) => Promise<FocusMode>;
  updateFocusMode: (modeId: string, config: Partial<FocusMode>) => Promise<FocusMode>;
  deleteFocusMode: (modeId: string) => Promise<void>;
  
  // Helpers
  shouldDeliverNotification: (notification: Notification, focusMode?: FocusMode) => boolean;
  getFocusModeTemplates: () => FocusModeTemplate[];
}

// ============================================================================
// NOTIFICATION BUILDER INTERFACES
// ============================================================================

export interface INotificationBuilder {
  // Content
  setTitle(title: string): this;
  setBody(body: string): this;
  setCategory(category: string): this;
  setLinkUrl(url: string): this;
  
  // Classification
  setChannel(channelNameOrId: string): this;
  setPriority(priority: NotificationPriority): this;
  setType(type: NotificationType): this;
  
  // App identification
  setApp(appId: string, appName: string, appIcon?: string): this;
  
  // Grouping
  setThread(threadId: string): this;
  setGroupKey(groupKey: string): this;
  
  // Behavior
  setSound(sound: string): this;
  requiresInteraction(required: boolean): this;
  setAutoDismiss(seconds: number): this;
  
  // Actions
  addAction(id: string, label: string, options?: Partial<NotificationAction>): this;
  clearActions(): this;
  
  // Media
  setMedia(media: NotificationMedia): this;
  setAvatar(url: string): this;
  
  // Scheduling
  schedule(date: Date | string): this;
  setExpiration(date: Date | string): this;
  
  // Metadata
  setMetadata(data: Record<string, any>): this;
  
  // Execution
  send(): Promise<Notification>;
  validate(): boolean;
}

// ============================================================================
// NOTIFICATION TEMPLATES
// ============================================================================

export interface NotificationTemplate {
  name: string;
  description: string;
  builder: (params: any) => INotificationBuilder;
}

// ============================================================================
// GROUPED NOTIFICATION INTERFACES
// ============================================================================

export interface GroupedNotification {
  groupKey: string;
  appName: string;
  appIcon?: string;
  notifications: Notification[];
  count: number;
  latestNotification: Notification;
  isExpanded: boolean;
  unreadCount: number;
}

export interface ThreadedNotification {
  threadId: string;
  title: string;
  notifications: Notification[];
  count: number;
  latestNotification: Notification;
  isExpanded: boolean;
  unreadCount: number;
}

// ============================================================================
// NOTIFICATION FILTER & SORT INTERFACES
// ============================================================================

export interface NotificationFilter {
  isRead?: boolean;
  priority?: NotificationPriority[];
  notificationType?: NotificationType[];
  appId?: string;
  channelId?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  hasActions?: boolean;
  isScheduled?: boolean;
}

export type NotificationSortBy = 
  | 'created_at' 
  | 'priority' 
  | 'app_name' 
  | 'is_read';

export type NotificationSortOrder = 'asc' | 'desc';

// ============================================================================
// UI STATE INTERFACES
// ============================================================================

export interface NotificationPanelState {
  isOpen: boolean;
  activeTab: 'all' | 'unread' | 'scheduled';
  filter?: NotificationFilter;
  sortBy: NotificationSortBy;
  sortOrder: NotificationSortOrder;
  expandedGroups: Set<string>;
  expandedThreads: Set<string>;
}

// ============================================================================
// ANALYTICS INTERFACES
// ============================================================================

export interface NotificationAnalytics {
  totalDelivered: number;
  totalRead: number;
  totalDismissed: number;
  totalActionsTaken: number;
  openRate: number; // percentage
  actionRate: number; // percentage
  dismissRate: number; // percentage
  averageTimeToRead: number; // milliseconds
  
  // Per-app stats
  appStats: {
    appId: string;
    appName: string;
    delivered: number;
    read: number;
    dismissed: number;
    openRate: number;
  }[];
  
  // Focus mode effectiveness
  focusModeStats: {
    modeId: string;
    modeName: string;
    notificationsBlocked: number;
    interruptionsSaved: number;
  }[];
  
  // Time-based
  peakTimes: {
    hour: number;
    count: number;
  }[];
  
  // Date range
  dateRange: {
    start: Date;
    end: Date;
  };
}

// ============================================================================
// SYSTEM DEFAULT CHANNELS
// ============================================================================

export const SYSTEM_CHANNELS = {
  ALARMS: '00000000-0000-0000-0000-000000000001',
  REMINDERS: '00000000-0000-0000-0000-000000000002',
  MESSAGES: '00000000-0000-0000-0000-000000000003',
  CALLS: '00000000-0000-0000-0000-000000000004',
  SYSTEM_UPDATES: '00000000-0000-0000-0000-000000000005',
  SECURITY_ALERTS: '00000000-0000-0000-0000-000000000006',
  TEAM_ACTIVITY: '00000000-0000-0000-0000-000000000007',
  CALENDAR: '00000000-0000-0000-0000-000000000008',
} as const;

// ============================================================================
// FOCUS MODE TEMPLATES
// ============================================================================

export const FOCUS_MODE_TEMPLATES: FocusModeTemplate[] = [
  {
    name: 'Do Not Disturb',
    icon_name: 'Moon',
    color: '#8b5cf6',
    description: 'Silence all notifications except urgent ones',
    do_not_disturb: true,
    allowed_channel_types: ['Alarms', 'Security Alerts'],
  },
  {
    name: 'Work',
    icon_name: 'Briefcase',
    color: '#3b82f6',
    description: 'Only work-related notifications',
    do_not_disturb: false,
    allowed_channel_types: ['Calendar', 'Messages', 'Calls', 'Team Activity'],
  },
  {
    name: 'Sleep',
    icon_name: 'Bed',
    color: '#6366f1',
    description: 'Only alarms and urgent security alerts',
    do_not_disturb: true,
    allowed_channel_types: ['Alarms', 'Security Alerts'],
  },
  {
    name: 'Personal',
    icon_name: 'User',
    color: '#ec4899',
    description: 'Only personal communications',
    do_not_disturb: false,
    allowed_channel_types: ['Messages', 'Calls'],
  },
];

// ============================================================================
// UTILITY TYPES
// ============================================================================

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];
